<template>
  <form
    class="s-button s-button-search"
    :class="{
      [`s-button-search--size-${size}`]: true,
      [`s-button--action-${actionType}`]: true,
      [`s-button--color-${color}`]: true,
      's-button--disabled s-button-search--disabled': disabled,
      's-button-search--always-open': props.alwaysOpen,
      's-button-search--fluid': props.fluid,
    }"
    @submit.prevent="submit"
  >
    <input
      v-model="term"
      :aria-label="ariaLabel"
      class="s-button-search__input"
      :disabled="disabled"
      name="term"
      :placeholder="placeholder"
      type="text"
      @blur="handleBlur"
      @focus="handleFocus"
    />
    <button class="s-button-search__button" :disabled="disabled" type="submit">
      <i class="fa-regular fa-magnifying-glass s-button-search__icon"></i>
    </button>
  </form>
</template>

<script setup lang="ts">
import type { UseButtonSearchEmit } from "@/lib/components/logic/atoms/useButtonSearch";

import * as useButtonSearch from "@/lib/components/logic/atoms/useButtonSearch";

import "./SButton.css";

const props = defineProps(useButtonSearch.props);
const emit = defineEmits<UseButtonSearchEmit>();

const { term, submit, handleFocus, handleBlur, disabled } = useButtonSearch.use(
  props,
  emit,
);
</script>

<style lang="postcss">
.s-button.s-button-search {
  @apply grid justify-end rounded-md transition;
  transition-property: background-color, border-color, width;
}

.s-button-search {
  &__input {
    @apply z-10 h-full w-full rounded-l bg-transparent text-sm opacity-0 outline-none area-[button];

    &:hover {
      @apply cursor-pointer;
    }

    &::placeholder {
      @apply invisible;
    }
  }

  &__button {
    @apply flex h-full w-full cursor-pointer items-center justify-center rounded-r transition-colors area-[button];
  }

  &__icon {
    @apply ml-0.5 leading-none;
  }

  &:focus-within,
  &--always-open {
    grid-template: "input button" / 1fr auto;

    .s-button-search__input {
      @apply opacity-100 transition-opacity area-[input];

      &::placeholder {
        @apply visible;
      }
    }
  }

  &--disabled {
    .s-button-search__input,
    .s-button-search__button {
      @apply cursor-not-allowed;
    }
  }

  &--size-lg {
    @apply h-12 w-12;
    grid-template: "button" / calc(theme("spacing.12"));

    &:focus-within,
    &.s-button-search--always-open {
      @apply w-96 pl-4;
      grid-template: "input button" / 1fr calc(theme("spacing.12"));
    }
  }

  &--size-md,
  &--size-sm {
    @apply h-10 w-10;
    grid-template: "button" / calc(theme("spacing.10"));

    &:focus-within,
    &.s-button-search--always-open {
      @apply w-72 pl-3;
      grid-template: "input button" / 1fr calc(theme("spacing.10"));
    }
  }

  &--fluid {
    @apply w-full;

    &:focus-within,
    &.s-button-search--always-open {
      @apply w-full;
    }
  }
}
</style>
